<template>
  <SimplePage title="予約詳細" :loading="reserveDetail.busy" showBack :onHome="back" @back="back">
    <div class="reserve-detail">
      <div v-if="customer.id || isMailSync || isResraku || reserve.freehandMemo" class="buttons">
        <Button :disabled="!customer.id" @click="customerModal = true"> <CustomerIcon />顧客情報 </Button>
        <Button :disabled="!isMailSync && !isResraku" @click="syncMessageModal = true">
          <MailIcon />{{ isResraku ? '連携履歴' : '連携メール' }}</Button
        >
        <Button :disabled="!reserve.freehandMemo" @click="lightbox = true">
          <img v-if="reserve.freehandMemo" class="image" :src="reserve.freehandMemo" alt="手書きメモ" />
          <template v-else><EditIcon />手書きメモ</template>
        </Button>
      </div>
      <ul class="history">
        <DetailList
          v-for="(detail, index) in detailList"
          :key="index"
          :detail="detail"
          :compare="compareData(index)"
          :isOpen="index === 0 ? true : false"
          :isSP="isSP"
          :hasDuplicatedIds="hasDuplicatedIds"
          :tebledUnsettled="tebledUnsettled"
        />
      </ul>
      <Customer
        class="customer"
        :customerDetail="customer"
        :freehandMemo="reserve.freehandMemo"
        :isCustomerDetailBusy="customerDetail.busy"
      />
    </div>
    <PageFooter
      class="footer"
      :class="{ noAction: (!isCanceled && !isCancelable) || (isCanceled && isBlock(reserve.type)) }"
    >
      <Button class="back" @click="back">戻る</Button>
      <div>
        <Button
          v-if="isCanceled && !isBlock(reserve.type)"
          danger
          @click="restoreModal = true"
          :disabled="gnaviMaintenance"
        >
          キャンセル取消
        </Button>
        <template v-else-if="isCancelable">
          <Button danger @click="showCancelModal" :disabled="checking || isResrakuUnsettled || gnaviMaintenance">予約キャンセル</Button>
          <Button primary v-if="resrakuTableSelect" @click="toTableEdit" :disabled="gnaviMaintenance">
            テーブル選択
          </Button>
          <StaffSelectPopover
            v-else-if="isUnsettled"
            @select="settle"
            :open="isShowStaffList"
            :disabled="!isShowStaffList"
            @auto-hide="isShowStaffList = false"
          >
            <Button primary :disabled="isShowStaffList || gnaviMaintenance" @click="onConfirm">予約確定</Button>
          </StaffSelectPopover>
          <Button v-else @click="editReserve" :disabled="checking || gnaviMaintenance">予約変更</Button>
        </template>
      </div>
    </PageFooter>
    <CustomerInfoModal v-if="customerModal" @close="customerModal = false" />
    <SyncMessageModal
      v-if="syncMessageModal"
      :mode="isResraku ? 'history' : 'mail'"
      @close="syncMessageModal = false"
    />
    <Lightbox v-if="lightbox" :handWriteImgPath="reserve.freehandMemo" @close="lightbox = false" backGround />
    <ReserveCancelModal @change="toReserveDateTable" />
    <CancelMailModal @sendDone="toReserveDateTable" />
    <ReserveRestoreModal
      v-if="restoreModal"
      :reserveId="reserve.id"
      @close="restoreModal = false"
      @change="reserveRestore"
    />
    <ResrakuMessageModal
      v-if="resrakuMessageModal"
      @close="resrakuMessageModal = false"
      :isResrakuReserve="isResrakuReserve"
      :isConfirmedResrakuReserve="isConfirmedResrakuReserve"
      isCancel
      :backTo="backTo"
    />
    <ResrakuSyncStatusVerifyModal
      v-if="ResrakuSyncStatusVerifyModal"
      @close="ResrakuSyncStatusVerifyModal = false"
      @confirm="settle"
      isSP
    />
  </SimplePage>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { formatDate, today, toHourClock } from '@/utils/date'
import { isResrakuReserve, isConfirmedResrakuReserve } from '@/utils/reserve'
import { isResrakuAlignment } from '@/utils/store'
import {
  syncFromGnavi,
  isInteractiveSyncType,
  syncFromOtherCompany,
  isGnavi,
} from '@/resources/reservation/syncTypeMap'
import { isResraku, isBlock } from '@/resources/reservation/typeMap'
import { checkStatus } from '@/resources/reservation/statusMap'
import SimplePage from '@/components/templates/SimplePage'
import CustomerInfoModal from '@/components/organisms/CustomerInfoModal'
import ReserveCancelModal from '@/components/organisms/ReserveCancelModal'
import ReserveRestoreModal from '@/components/organisms/ReserveRestoreModal'
import CancelMailModal from '@/components/organisms/CancelMailModal'
import SyncMessageModal from '@/components/organisms/SyncMessageModal'
import ResrakuMessageModal from '@/components/organisms/ResrakuMessageModal'
import ResrakuSyncStatusVerifyModal from '@/components/organisms/ResrakuSyncStatusVerifyModal'
import StaffSelectPopover from '@/components/organisms/StaffSelectPopover'
import Lightbox from '@/components/molecules/Lightbox'
import Dialog from '@/components/molecules/ModalDialog'
import PageFooter from '@/components/molecules/PageFooter'
import Button from '@/components/atoms/Button'
import CustomerIcon from '@/components/icons/customer'
import EditIcon from '@/components/icons/edit'
import MailIcon from '@/components/icons/mail'
import DetailList from './DetailList'
import Customer from './Customer'

export default {
  name: 'ReservationHistoryDetail',
  components: {
    SimplePage,
    DetailList,
    Customer,
    CustomerInfoModal,
    ReserveCancelModal,
    CancelMailModal,
    ReserveRestoreModal,
    SyncMessageModal,
    ResrakuMessageModal,
    ResrakuSyncStatusVerifyModal,
    Lightbox,
    StaffSelectPopover,
    PageFooter,
    Button,
    CustomerIcon,
    EditIcon,
    MailIcon,
  },
  props: {
    id: Number,
    alertType: Number,
    backTo: String,
  },
  data() {
    return {
      GNAVI_MAINTENANCE,
      customerModal: false,
      restoreModal: false,
      syncMessageModal: false,
      settleDisabled: false,
      lightbox: false,
      alertNum: this.alertType || null,
      resrakuMessageModal: false,
      ResrakuSyncStatusVerifyModal: false,
      isShowStaffList: false,
    }
  },
  computed: {
    ...mapState({
      isSP: state => state.app.isSP,
      currentStore: state => state.stores.current.store,
      reserveDetail: state => state.reserves.detail,
      reserve: state => state.reserves.detail.reservation,
      detailList: state => state.reserves.history.details,
      customerDetail: state => state.customers.detail,
      customer: state => state.customers.detail.customer || {},
      restoreCautionType: state => state.reserves.detail.cautionType,
    }),
    ...mapGetters({
      allStaffs: 'staffs/all',
    }),
    isMailSync() {
      return !!syncFromOtherCompany[this.reserve.syncType]
    },
    isResraku() {
      return isResraku(this.reserve.type)
    },
    isCancelable() {
      return this.reserve.status > 1
    },
    isCanceled() {
      return this.reserve.id && this.reserve.status <= 1 && !syncFromGnavi[this.reserve.syncType] && !this.isMailSync
    },
    hasDuplicatedIds() {
      return !!this.reserveDetail.reservation.duplicatedIds?.length
    },
    tebledUnsettled() {
      const { tableIds } = this.reserveDetail.reservation
      return tableIds.includes(-1) && tableIds.length === 1
    },
    isResrakuReserve() {
      return isResrakuReserve(this.reserve)
    },
    isConfirmedResrakuReserve() {
      return isConfirmedResrakuReserve(this.reserve)
    },
    isUnsettled() {
      return checkStatus.isUnsettled(this.reserve.status)
    },
    checking() {
      return this.reserve.resrakuHandlingProcess === 1
    },
    resrakuCautionType() {
      const type = this.reserve.resrakuHandlingProcess
      const checked = this.reserve.resrakuModalChecked
      return (this.isResrakuReserve.isResraku || this.isResrakuReserve.isResrakuOzmollTabelog) &&
        ![0, 2].includes(type) &&
        !checked
        ? type
        : false
    },
    isResrakuAlignment() {
      return isResrakuAlignment(this.currentStore)
    },
    resrakuTableSelect() {
      return this.isResrakuReserve.hotpepper || this.isResrakuReserve.inquiry || this.isResrakuReserve.retty
    },
    gnaviMaintenance() {
      return GNAVI_MAINTENANCE && isGnavi(this.reserve.syncType)
    },
    isResrakuUnsettled() {
      return this.isResraku && this.reserve.status === 3
    },
  },
  async created() {
    const { commit } = this.$store
    commit('reserves/history/clear')
    commit('reserves/detail/clear')
    await this.fetch()
    if (this.isResrakuUnsettled) {
      Dialog.show({
        title: 'レスラクから連携された予約',
        message:
          'レスラクからの連携予約については、\n予約時に席在庫が確保されています。\n\nテーブル未指定の状態ではキャンセルできません。\nキャンセルする場合は、席指定を行ってください。',
        okText: '閉じる',
      })
    }
  },
  methods: {
    isBlock,
    async fetch() {
      const { dispatch } = this.$store
      await Promise.all([
        dispatch('reserves/history/get', { rsvId: this.id }),
        dispatch('reserves/detail/get', { id: this.id, alertType: this.alertNum || null, includeCancel: true }),
      ])
      if (this.resrakuCautionType) this.resrakuCaution()
      if (this.gnaviMaintenance) {
        Dialog.show({
          title: 'ご注意',
          message:
            'ただいま「ぐるなびネット予約」がメンテナンス中につき、こちらの操作は行えません。\nメンテナンス解除まで、今しばらくお待ちください。\nメンテナンスの詳細についてはお知らせをご確認ください。',
          okText: '閉じる',
        })
      }
    },
    back() {
      if (this.isSP) {
        this.$router.push(this.backTo)
      } else {
        location.href = this.backTo
      }
    },
    async showCancelModal() {
      const { commit } = this.$store
      const { id, reserveDatetime, syncRsvId, syncType, status } = this.reserve
      if (this.showResrakuMessageModal(reserveDatetime)) {
        // レスラク連携ぐるなびネット予約 or ホットペッパー or Retty
        this.resrakuMessageModal = true
      } else if (
        (!this.isResrakuAlignment && this.isResrakuReserve.isResraku && status === 2) ||
        !syncRsvId ||
        !isInteractiveSyncType(syncType) ||
        !this.currentStore.syncStatus.gnavi ||
        toHourClock(reserveDatetime).date < today()
      ) {
        // 予約キャンセル
        commit('reserves/cancel/show', id)
      } else {
        // キャンセルメール
        commit('reserves/cancelMail/show', id)
      }
    },
    editReserve(step) {
      if (this.isSP) {
        this.$router.push(`/sp/reserve/${this.reserve.id}/edit`)
      } else {
        location.href = `/reservation/edit/${step.type ? 'init' : step}?reservation_id=${this.reserve.id}`
      }
    },
    compareData(index) {
      let data = {}
      const last = this.detailList.length - 1
      if (index === last) {
        data = this.detailList[index]
      } else {
        data = this.detailList[index + 1]
      }
      return data
    },
    toReserveDateTable() {
      if (this.isSP) {
        this.alertNum = -1
        this.fetch()
      } else {
        location.assign(
          `/reservation/${this.currentStore.reservesView}?date=${formatDate(
            toHourClock(this.reserve.reserveDatetime).date,
            'YYYYMMDD',
          )}`,
        )
      }
    },
    async onConfirm() {
      if (this.isResrakuReserve.request && !this.isResrakuAlignment) {
        this.ResrakuSyncStatusVerifyModal = true
      } else {
        this.isShowStaffList = true
      }
    },
    async settle({ id }) {
      this.isShowStaffList = false
      if (this.isResrakuReserve.request && this.isResrakuAlignment) {
        await this.$store.dispatch('resraku/request/accept', {
          rsvId: this.reserve.id,
          staffId: id,
          tableList: this.reserve.tableIds.sort().join(),
        })
      } else {
        await this.$store.dispatch('reserves/detail/settle', id)
      }
      this.fetch()
    },
    async reserveRestore() {
      await this.fetch()

      if (this.restoreCautionType) {
        const cautionPath = `reserve/restore/caution[${this.restoreCautionType}]`
        if (
          await Dialog.confirm({
            title: this.$t(`${cautionPath}.title`),
            message: this.$t(`${cautionPath}.message`),
            caution: true,
          })
        ) {
          this.$store.commit('reserves/detail/updateRestoreCaution', null)
          this.editReserve({ layout: 'layout', table_list: 'table' }[this.currentStore.reservesView])
        }
      }
    },
    async toTableEdit() {
      if (!this.isResrakuAlignment) {
        if (
          !(await Dialog.confirm({
            title: 'テーブル選択',
            message:
              'レスラクとの連携が「未連携」または\n「一時停止」「停止」になっています。\n媒体管理画面にて操作してください。\n\n台帳上での変更は可能ですが、\nレスラク及び媒体へは連携されません。\n\nテーブル選択を継続しますか？',
            okText: '確定',
            cancelText: '戻る',
            caution: true,
          }))
        ) {
          return
        }
      }
      if (this.isSP) {
        this.$router.push(`/sp/reserve/${this.reserve.id}/edit`)
      } else if (this.currentStore.reservesView === 'layout') {
        location.href = `/reservation/layout/reserve/${this.reserve.id}/edit`
      } else {
        location.href = `/reservation/table_list/reserve/${this.reserve.id}/editTable`
      }
    },
    async resrakuCaution() {
      const cautionPath = `reserve/resraku/caution[${this.resrakuCautionType}]`
      if (
        await Dialog.show({
          title: this.$t(`${cautionPath}.title`),
          message: this.$t(`${cautionPath}.message`),
          description: this.$t(`${cautionPath}.description`),
          descriptionSize: 13,
          descriptionPosition: [3, 4].includes(this.resrakuCautionType) ? 'left' : 'center',
          okText: '閉じる',
        })
      ) {
        this.$store.dispatch('resraku/request/checked', {
          rsvId: this.id,
          resrakuHandlingProcess: this.reserve.resrakuHandlingProcess,
        })
      }
    },
    showResrakuMessageModal(reserveDatetime) {
      return (
        this.isResrakuAlignment &&
        (this.isConfirmedResrakuReserve.confirmed ||
          this.isResrakuReserve.hotpepper ||
          this.isResrakuReserve.retty ||
          ((this.isResrakuReserve.inquiry || this.isResrakuReserve.request) &&
            today() <= toHourClock(reserveDatetime).date))
      )
    },
  },
}
</script>

<style lang="postcss" scoped>
@custom-media --small-width (width < 840px);

>>> main {
  padding-bottom: 72px;
}

.footer {
  > div {
    display: grid;
    grid-auto-columns: 1fr;
    grid-auto-flow: column;
    grid-gap: 8px;
  }

  .responsive & {
    @media screen and (max-width: 639px) {
      &.noAction {
        display: none;
      }

      > div {
        flex: 1;
        grid-gap: 16px;
      }

      .back {
        display: none;
      }
    }
  }
}

.reserve-detail {
  box-sizing: border-box;
  display: grid;
  grid-gap: 16px;
  grid-template-columns: 1fr auto;
  -webkit-overflow-scrolling: touch;
  padding: 16px;
  position: relative;

  .responsive & {
    @media screen and (--small-width) {
      grid-gap: 16px 8px;
      grid-template-columns: 1fr;
    }
  }
}

.buttons {
  display: none;
  grid-gap: 8px;
  grid-template-columns: 1fr 1fr 2fr;

  .button {
    align-items: center;
    display: flex;
    flex-direction: column;
    font-size: 11px;
    height: 60px;
    justify-content: center;
    line-height: 1;
    overflow: hidden;
    padding: 4px;
    width: auto;

    &:disabled {
      fill: var(--very-light-gray);
    }

    svg {
      margin-bottom: 4px;
    }
  }

  .responsive & {
    @media screen and (--small-width) {
      display: grid;
    }
  }
}

.image {
  background: var(--white);
  border: 1px solid var(--border-color);
  box-sizing: border-box;
  height: 50px;
  object-fit: contain;
  width: 100%;
}

.history {
  box-sizing: border-box;
  margin-bottom: 72px;
}

.customer {
  height: fit-content;
  position: sticky;
  top: 16px;

  .responsive & {
    @media screen and (--small-width) {
      display: none;
    }
  }
}
</style>
